import { Link } from "react-router-dom";
import { Trophy, ExternalLink, Award } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import PremiumBadge from "./PremiumBadge";
import UnitPricesInput from "./UnitPricesInput";

export default function BidList({
  bids, order, canBid, myBid, isRestOwner,
  bidOpen, setBidOpen, bidPrice, setBidPrice, bidHours, setBidHours, bidNotes, setBidNotes,
  unitPrices, setUnitPrices, onSubmit, onSelect, isSupplierViewer = false,
}) {
  const canAward = isRestOwner && order.status === "open";
  const earlyAward = canAward && new Date(order.auction_ends_at) > new Date();
  return (
    <div className="ga-card p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-slate-900">Offerte ({bids.length})</h2>
        {canBid && (
          <Dialog open={bidOpen} onOpenChange={setBidOpen}>
            <DialogTrigger asChild>
              <Button className="ga-btn-primary rounded-full" data-testid="open-bid-modal">{myBid ? "Aggiorna offerta" : "Invia offerta"}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{myBid ? "Aggiorna offerta" : "Invia offerta"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {order.items?.length > 0 && <UnitPricesInput items={order.items} unitPrices={unitPrices} setUnitPrices={setUnitPrices} setTotal={setBidPrice} />}
                <div>
                  <Label>Prezzo totale (€)</Label><Input type="number" step="0.01" min="0" value={bidPrice} onChange={e => setBidPrice(e.target.value)} data-testid="bid-price-input" />
                  {myBid && <div className="mt-1 text-xs text-slate-500" data-testid="bid-lower-only-hint">Puoi solo ribassare: la tua offerta attuale è € {myBid.price.toFixed(2)}, inserisci un prezzo più basso.</div>}
                  {order.express_max_price && <div className="mt-1 text-xs text-slate-500" data-testid="bid-express-cap-hint">Ordine Express: prezzo massimo € {order.express_max_price.toFixed(2)}.</div>}
                </div>
                <div><Label>Tempi consegna (ore)</Label><Input type="number" min={1} value={bidHours} onChange={e => setBidHours(e.target.value)} data-testid="bid-hours-input" /></div>
                <div><Label>Note (opzionale)</Label><Textarea rows={2} value={bidNotes} onChange={e => setBidNotes(e.target.value)} data-testid="bid-notes-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={onSubmit} className="ga-btn-primary rounded-full" data-testid="submit-bid-button">Invia</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isSupplierViewer && order.status === "open" && !order.choice_ends_at && (
        <div className="mt-2 text-xs text-slate-500" data-testid="bids-visibility-hint">{order.auction_visibility === "sealed" ? "Asta a busta chiusa: vedi solo la tua offerta." : "Asta trasparente: vedi i prezzi degli altri fornitori in tempo reale, senza i nomi."}</div>
      )}
      {earlyAward && bids.length > 0 && (
        <div className="mt-2 text-xs text-slate-500" data-testid="early-award-hint">Prima della scadenza puoi chiudere l'asta solo accettando la miglior offerta.</div>
      )}
      {canAward && order.choice_ends_at && (
        <div className="mt-2 text-xs text-amber-800" data-testid="choice-window-hint">L'asta è scaduta: scegli un'offerta entro le {new Date(order.choice_ends_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}, per esempio per tempi di consegna migliori. Altrimenti vince automaticamente la miglior offerta.</div>
      )}
      {isSupplierViewer && order.choice_ends_at && (
        <div className="mt-2 text-xs text-slate-500" data-testid="choice-window-supplier-hint">Asta chiusa: in attesa della scelta del ristorante. Non si possono più inviare offerte.</div>
      )}
      {bids.length === 0 && <div className="mt-4 text-sm text-slate-500">Ancora nessuna offerta.</div>}

      <div className="mt-4 space-y-3">
        {bids.map((b, idx) => (
          <div key={b.id} className={`p-4 rounded-xl border ${idx === 0 && order.status === "open" ? "border-emerald-500/40 bg-emerald-50/50" : "border-slate-200 bg-white"}`} data-testid={`bid-row-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {b.supplier?.anonymous ? (
                  <div className="font-semibold text-slate-700 inline-flex items-center gap-1.5" data-testid={`bid-anon-${b.id}`}>{b.supplier.name} <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">anonimo</span></div>
                ) : (
                <Link to={`/fornitore/profilo/${b.supplier?.id}`} className="font-semibold text-slate-900 truncate hover:underline inline-flex items-center gap-1.5 flex-wrap" data-testid={`bid-supplier-link-${b.id}`}>
                  {b.supplier?.name}{myBid?.id === b.id && <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">(tu)</span>}
                  <ExternalLink className="w-3 h-3 text-slate-400" />
                  {b.supplier?.is_premium && <PremiumBadge testId={`bid-premium-badge-${b.id}`} />}
                </Link>
                )}
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  {!b.supplier?.anonymous && <span>★ {b.supplier?.rating_avg || 0} ({b.supplier?.rating_count || 0})</span>}
                  <span>· {b.delivery_time_hours}h consegna</span>
                  {b.supplier?.loyalty_stars > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-indigo-700 font-semibold" data-testid={`bid-loyalty-${b.id}`}>
                      <Award className="w-3 h-3" /> Fedeltà {b.supplier.loyalty_stars}★
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-bold text-lg text-emerald-700">€ {b.price.toFixed(2)}</div>
                {idx === 0 && order.status === "open" && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Miglior offerta</div>
                )}
              </div>
            </div>
            {b.notes && <div className="mt-2 text-xs text-slate-600">{b.notes}</div>}
            {b.unit_prices?.some(v => v != null) && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`bid-unit-prices-${b.id}`}>
                {order.items.map((it, i) => b.unit_prices[i] != null && <span key={i} className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[11px] font-mono">{it.name}: € {Number(b.unit_prices[i]).toFixed(2)}/{it.unit}</span>)}
              </div>
            )}
            {canAward && (!earlyAward || idx === 0) && (
              <div className="mt-3">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => onSelect(b.id)} data-testid={`select-bid-${b.id}`}>
                  <Trophy className="w-3.5 h-3.5 mr-1.5" />Aggiudica manualmente
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
